import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import React, { Fragment, useCallback } from 'react';
import {
  compose,
  withState,
  withHandlers,
} from 'react-recompose';
// common
import { menuRoutesConfig } from '../../../../common/settings';
// features
import { setTabToQuery } from '../../../configurations/helpers';
import { setActiveConfigGroupWithTab } from '../../../configurations/actions';
// helpers/constants
import * as G from '../../../../helpers';
// icons
import * as I from '../../../../svgs';
// ui
import { Box } from '../../../../ui';
// feature navbar-menu
import * as H from '../../helpers';
import configurationTabs, { actionToGroupMap } from '../../configs/configuration-tabs';
import {
  TabItem,
  HrDivider,
  IconWrapper,
  SidebarItem,
  SidebarGroupWrapper,
  SidebarItemRightPlusIcon,
  SidebarItemRightArrowIcon,
} from './ui';
//////////////////////////////////////////////////

const renderArrowIcon = (opened, iconColor) => (
  <SidebarItemRightArrowIcon>
    {
      G.ifElse(
        G.isTrue(opened),
        I.arrowUpSimple2,
        I.arrowDownSimple2,
      )(iconColor)
    }
  </SidebarItemRightArrowIcon>
);

const renderPlusIcon = (handleClickPlusIcon, item, iconColor) => (
  <SidebarItemRightPlusIcon
    color={iconColor}
    onClick={(event) => {
      G.stopPropagation(event);
      handleClickPlusIcon(item);
    }}
  >
    { I.plusInCircleFilled(iconColor, 14, 14) }
  </SidebarItemRightPlusIcon>
);

const hasMenuItemPermissions = (item, authorities) => {
  const {
    permissions,
    andPermissions,
    allPermissions,
  } = item;

  return G.isAllTrue(
    R.or(
      G.isNilOrEmpty(permissions),
      G.hasAmousCurrentUserPermissions(permissions),
    ),
    R.or(
      G.isNilOrEmpty(andPermissions),
      G.hasAmousCurrentUserPermissions(andPermissions),
    ),
    R.or(
      G.isNilOrEmpty(allPermissions),
      G.checkAllPermissions(allPermissions, authorities),
    ),
  );
};

const renderSubMenu = (submenu, subMenuProps) => submenu.map((item, index) => (
  <SidebarItemComponent {...subMenuProps} key={index} item={item} />
));

const enhance = compose(
  withState('opened', 'toggleItem', false),
  withState('isHovered', 'setHoverStatus', false),
  withHandlers({
    handleToggleItem: ({
      item,
      opened,
      toggleItem,
      subMenusStatus,
      toggleSubmenus,
    }) => () => {
      const {
        key,
        ownerItemGuid,
      } = item;

      const open = R.propEq(false, key, subMenusStatus);

      if (R.isNil(ownerItemGuid)) {
        const newStatus = R.mapObjIndexed(
          (value, oKey) => {
            if (open) {
              return G.ifElse(R.equals(oKey, key), true, false);
            }

            return G.ifElse(R.equals(oKey, key), false, value);
          },
          subMenusStatus,
        );

        return toggleSubmenus(newStatus);
      }

      toggleItem(R.not(opened));
    },
    onHidePopup: ({ setShowPopup }) => (e) => {
      G.stopPropagation(e);
      setShowPopup(false);
    },
  }),
);

const TabItems = ({
  item,
  color,
  currentTab,
  currentItem,
  setSidebarStatus,
}) => {
  const dispatch = useDispatch();

  const { tabs, action } = item;

  const isActive = R.equals(currentItem, action);
  const configTabs = R.propOr([], action, configurationTabs);
  const configurationUri = R.path([action, 'paths', 0], menuRoutesConfig);

  const goToTab = useCallback((tabId) => {
    const tab = H.getIndexByTabId(tabId, configTabs);

    dispatch(setActiveConfigGroupWithTab({
      tab,
      group: R.propOr(null, action, actionToGroupMap),
    }));

    if (isActive) {
      setTabToQuery(tabId);
    } else {
      G.goToRoute(`${configurationUri}?tab=${tabId}`);
    }

    setSidebarStatus(false);
  }, [isActive, configTabs, configurationUri]);

  return (
    <Box>
      { tabs.map((tab) => {
        const { text, tabId } = tab;

        return (
          <TabItem
            key={tabId}
            color={color}
            onClick={() => goToTab(tabId)}
            active={R.and(isActive, R.equals(tabId, currentTab))}
          >
            {text}
          </TabItem>
        );
      })}
    </Box>
  );
};

const SidebarItemComponent = enhance((props) => {
  const {
    item,
    opened,
    styling,
    navItems,
    isHovered,
    iconsColor,
    currentTab,
    authorities,
    setHoverStatus,
    subMenusStatus,
    toggleSubmenus,
    pinnedMenuItems,
    currentMenuItem,
    handleToggleItem,
    setSidebarStatus,
    handleClickNavItem,
    handleClickPlusIcon,
  } = props;

  const {
    key,
    action,
    title,
    submenu,
    iconName,
    permissions,
    isSubmenuItem,
    andPermissions,
    allPermissions,
    containedSubItems,
  } = item;

  const hasNotPermissions = R.not(hasMenuItemPermissions({
    permissions,
    andPermissions,
    allPermissions,
  }, authorities));

  if (hasNotPermissions) return null;

  const subMenuProps = {
    styling,
    navItems,
    currentTab,
    iconsColor,
    authorities,
    subMenusStatus,
    toggleSubmenus,
    currentMenuItem,
    pinnedMenuItems,
    setSidebarStatus,
    handleClickNavItem,
    handleClickPlusIcon,
  };

  if (G.isNotEmpty(submenu)) {
    if (R.not(
      R.any(
        (subMenuItem) => hasMenuItemPermissions(subMenuItem, authorities),
      )(submenu))
    ) return null;

    const isActive = R.any(R.propEq(currentMenuItem, 'action'), submenu);

    return (
      <SidebarGroupWrapper opened={R.or(subMenusStatus[key], opened)}>
        <SidebarItem
          active={isActive}
          color={iconsColor}
          onClick={handleToggleItem}
        >
          <IconWrapper>
            {
              R.pathOr(
                () => {},
                [iconName],
                I,
              )(iconsColor, null, null, styling.backgroundColor)}
          </IconWrapper>
          {title}
          {renderArrowIcon(R.or(subMenusStatus[key], opened), iconsColor)}
        </SidebarItem>
        {renderSubMenu(submenu, subMenuProps)}
        <HrDivider m='8px' borderColor={iconsColor} />
      </SidebarGroupWrapper>
    );
  }

  return (
    <Fragment>
      <SidebarItem
        color={iconsColor}
        disabled={containedSubItems}
        isSubmenuItem={isSubmenuItem}
        onMouseEnter={() => setHoverStatus(true)}
        active={R.equals(currentMenuItem, action)}
        onMouseLeave={() => setHoverStatus(false)}
        onClick={() => handleClickNavItem(action)}
      >
        <IconWrapper>
          {
            R.pathOr(
              () => {},
              [iconName],
              I,
            )(iconsColor, null, null, styling.backgroundColor)}
        </IconWrapper>
        {title}
        {
          G.renderIfAll(
            renderPlusIcon(handleClickPlusIcon, item, iconsColor),
            isHovered,
            R.not(containedSubItems),
            G.notInList(pinnedMenuItems, key),
            R.not(R.equals(key, 'menu:dispatch-board-beta')),
          )
        }
      </SidebarItem>
      { G.isNotNilAndNotEmpty(R.prop('tabs', item)) && (
        <TabItems
          item={item}
          color={iconsColor}
          currentTab={currentTab}
          currentItem={currentMenuItem}
          setSidebarStatus={setSidebarStatus}
        />
      )}
    </Fragment>
  );
});

export default SidebarItemComponent;
